/**
 * Camunda 7.18 Filter REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/filter/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Filter,
  FilterQuery,
  FilterDto,
  CountResult,
} from '../types';

export class FilterService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/filter';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get filters
   */
  async getList(query?: FilterQuery): Promise<Filter[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Filter[]>(`${this.basePath}${params}`);
  }

  /**
   * Get filters count
   */
  async getCount(query?: FilterQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get filter by ID
   */
  async getById(id: string, itemCount?: boolean): Promise<Filter> {
    const params = itemCount ? '?itemCount=true' : '';
    return this.client.get<Filter>(`${this.basePath}/${id}${params}`);
  }

  // ============================================================================
  // Create
  // ============================================================================

  /**
   * Create a new filter
   */
  async create(dto: FilterDto): Promise<Filter> {
    return this.client.post<Filter>(`${this.basePath}/create`, dto);
  }

  // ============================================================================
  // Update
  // ============================================================================

  /**
   * Update a filter
   */
  async update(id: string, dto: FilterDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}`, dto);
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete a filter
   */
  async delete(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Execute Filter
  // ============================================================================

  /**
   * Execute filter (get list)
   */
  async executeList<T>(
    id: string,
    options?: {
      firstResult?: number;
      maxResults?: number;
    }
  ): Promise<T[]> {
    const params = new URLSearchParams();
    if (options?.firstResult !== undefined) params.append('firstResult', String(options.firstResult));
    if (options?.maxResults !== undefined) params.append('maxResults', String(options.maxResults));
    const queryString = params.toString();
    return this.client.get<T[]>(`${this.basePath}/${id}/list${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Execute filter with extended query (POST)
   */
  async executeListPost<T>(
    id: string,
    extendingQuery?: Record<string, any>,
    options?: {
      firstResult?: number;
      maxResults?: number;
    }
  ): Promise<T[]> {
    const params = new URLSearchParams();
    if (options?.firstResult !== undefined) params.append('firstResult', String(options.firstResult));
    if (options?.maxResults !== undefined) params.append('maxResults', String(options.maxResults));
    const queryString = params.toString();
    return this.client.post<T[]>(
      `${this.basePath}/${id}/list${queryString ? `?${queryString}` : ''}`,
      extendingQuery || {}
    );
  }

  /**
   * Execute filter count
   */
  async executeCount(id: string): Promise<CountResult> {
    return this.client.get<CountResult>(`${this.basePath}/${id}/count`);
  }

  /**
   * Execute filter count with extended query (POST)
   */
  async executeCountPost(id: string, extendingQuery?: Record<string, any>): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/${id}/count`, extendingQuery || {});
  }

  /**
   * Execute single result
   */
  async executeSingleResult<T>(id: string): Promise<T> {
    return this.client.get<T>(`${this.basePath}/${id}/singleResult`);
  }

  /**
   * Execute single result with extended query (POST)
   */
  async executeSingleResultPost<T>(id: string, extendingQuery?: Record<string, any>): Promise<T> {
    return this.client.post<T>(`${this.basePath}/${id}/singleResult`, extendingQuery || {});
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: FilterQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let filterServiceInstance: FilterService | null = null;

export function getFilterService(): FilterService {
  if (!filterServiceInstance) {
    filterServiceInstance = new FilterService();
  }
  return filterServiceInstance;
}

export default FilterService;
